import { useState } from 'react';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { siteInfo, faqs, testimonials, stats } from '@/mocks/comics';

export default function AboutPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMsg, setFormMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMsg('感谢您的留言，我们会尽快回复！');
      form.reset();
      return;
    }

    formData.delete('website_alt');

    try {
      const res = await fetch('https://readdy.ai/api/form/d9p1gdgsenbnioleso00', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (res.ok && parsed && (parsed as { code?: string }).code === 'OK') {
        setFormStatus('success');
        setFormMsg('感谢您的留言，我们会尽快回复！');
        form.reset();
      } else {
        const serverMsg = (parsed as { meta?: { message?: string; detail?: string } })?.meta?.message
          || (parsed as { message?: string })?.message
          || (parsed as { meta?: { detail?: string } })?.meta?.detail
          || '提交失败，请稍后再试';
        setFormStatus('error');
        setFormMsg(String(serverMsg));
      }
    } catch {
      setFormStatus('error');
      setFormMsg('网络错误，请检查网络后重试');
    }
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `关于我们 - ${siteInfo.name}`,
    description: siteInfo.description,
    url: 'https://manhwaplanet.com/about',
    isPartOf: {
      '@type': 'WebSite',
      name: siteInfo.name,
      url: 'https://manhwaplanet.com',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <SEOHead
        title={`关于我们 - ${siteInfo.name}`}
        description={siteInfo.description}
        keywords={`韩漫网站,关于韩漫星球,韩国漫画平台,正版韩漫,${siteInfo.name}`}
        canonicalUrl="https://manhwaplanet.com/about"
        schema={{ ...aboutSchema, ...faqSchema }}
      />

      <Breadcrumb />

      <main className="max-w-[1400px] mx-auto px-4 md:px-6 py-6">
        {/* Hero */}
        <section className="text-center py-12 md:py-16">
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">关于韩漫星球</h1>
          <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
            {siteInfo.description}
          </p>
        </section>

        {/* Stats */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16">
          {stats.map((stat) => (
            <div key={stat.label} className="bg-background-100 rounded-xl p-6 text-center">
              <div className="text-2xl md:text-3xl font-heading font-bold text-primary-600">{stat.value}</div>
              <div className="text-xs md:text-sm text-foreground-600 mt-2">{stat.label}</div>
            </div>
          ))}
        </section>

        {/* Brand Story */}
        <section className="mb-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">品牌故事</h2>
              <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                <p>
                  韩漫星球成立于2020年，是国内领先的<strong>韩国漫画在线观看平台</strong>。自创立以来，我们始终秉持"版权合规、内容为王"的核心理念，致力于为华语地区的韩漫爱好者提供<strong>海量正版韩漫资源</strong>。
                </p>
                <p>
                  平台创立之初，我们便与韩国多家顶级漫画出版机构和独立创作者建立了深度合作关系，确保每一部上架作品都经过严格的正版授权流程。目前，平台已收录超过<strong>10,000部</strong>正版韩漫，涵盖恋爱、动作、奇幻、搞笑、悬疑、剧情、日常、古风等多种类型。
                </p>
                <p>
                  我们采用<strong>下拉式韩漫</strong>排版技术，完美适配手机、平板和桌面端浏览器，用户无需下载任何应用即可享受流畅的沉浸式阅读体验。平台日均更新超过200话内容，累计服务超过500万用户。
                </p>
                <p>
                  韩漫星球的愿景是成为全球最受信赖的韩漫阅读平台，让每一位漫画爱好者都能在合法、安全、高品质的环境中畅享精彩的韩国漫画世界。
                </p>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20cozy%20reading%20space%20with%20large%20bookshelf%20filled%20with%20colorful%20comic%20books%2C%20warm%20ambient%20lighting%20from%20table%20lamp%2C%20comfortable%20armchair%2C%20digital%20tablet%20displaying%20webtoon%2C%20minimalist%20interior%20design%2C%20soft%20coral%20and%20cream%20color%20palette%2C%20cozy%20atmosphere%2C%20warm%20photographic%20style&width=800&height=600&seq=about-brand-story&orientation=landscape"
                alt="韩漫星球品牌故事"
                className="w-full h-full object-cover"
                width={800}
                height={600}
              />
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="mb-16">
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">我们的承诺</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              { icon: 'ri-shield-check-line', title: '正版授权', desc: '所有漫画均获得正版授权，为用户提供合法安心的阅读环境，坚决维护创作者权益。' },
              { icon: 'ri-stack-line', title: '海量资源', desc: '收录超过10,000部韩漫作品，类型丰富多样，满足不同读者的口味和需求。' },
              { icon: 'ri-smartphone-line', title: '极致体验', desc: '下拉式阅读设计，完美适配各种设备，无需下载应用，打开浏览器即可畅享阅读。' },
            ].map((v) => (
              <div key={v.title} className="bg-background-100 rounded-xl p-6 text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100">
                  <i className={`${v.icon} text-2xl text-primary-600`}></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-900 mb-2">{v.title}</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* User Reviews */}
        <section className="mb-16">
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">用户口碑</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t) => (
              <div key={t.id} className="bg-background-100 rounded-xl p-6">
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: t.rating }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">{t.comment}</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                    <img src={t.avatar} alt={t.name} className="w-full h-full object-cover" width={40} height={40} />
                  </div>
                  <span className="text-sm font-medium text-foreground-900">{t.name}</span>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-16">
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">常见问题</h2>
          <div className="max-w-3xl mx-auto space-y-3">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-background-100 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                >
                  <span className="text-sm font-medium text-foreground-900 pr-4">{faq.question}</span>
                  <i className={`${openFaq === index ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-foreground-500 flex-shrink-0 transition-transform duration-200`}></i>
                </button>
                {openFaq === index && (
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Contact Form */}
        <section className="mb-16">
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">联系我们</h2>
          <div className="max-w-2xl mx-auto">
            <div className="bg-background-100 rounded-2xl p-6 md:p-8">
              {formStatus === 'success' ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100">
                    <i className="ri-check-line text-2xl text-accent-600"></i>
                  </div>
                  <p className="text-foreground-900 font-medium">{formMsg}</p>
                  <button
                    onClick={() => setFormStatus('idle')}
                    className="mt-4 text-sm text-primary-600 hover:text-primary-700 cursor-pointer whitespace-nowrap"
                  >
                    继续留言
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-readdy-form="true" id="contact-form">
                  <input type="text" name="website_alt" className="spam-field" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>
                  <div className="mb-4">
                    <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                    <input
                      id="contact-subject"
                      name="subject"
                      type="text"
                      required
                      className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300"
                      placeholder="请输入主题"
                    />
                  </div>
                  <div className="mb-5">
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言</label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 resize-none"
                      placeholder="请输入您的留言（最多500字）"
                    ></textarea>
                  </div>
                  {formStatus === 'error' && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                      {formMsg}
                    </div>
                  )}
                  <button
                    type="submit"
                    className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium rounded-full transition-colors duration-200 cursor-pointer whitespace-nowrap"
                  >
                    发送留言
                  </button>
                </form>
              )}
            </div>

            {/* Contact Info */}
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="text-center p-4">
                <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-mail-line text-foreground-600"></i>
                </div>
                <p className="text-xs text-foreground-500">邮箱</p>
                <p className="text-sm text-foreground-900">{siteInfo.email}</p>
              </div>
              <div className="text-center p-4">
                <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-phone-line text-foreground-600"></i>
                </div>
                <p className="text-xs text-foreground-500">电话</p>
                <p className="text-sm text-foreground-900">{siteInfo.phone}</p>
              </div>
              <div className="text-center p-4">
                <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-map-pin-line text-foreground-600"></i>
                </div>
                <p className="text-xs text-foreground-500">地址</p>
                <p className="text-sm text-foreground-900">{siteInfo.address}</p>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}